/**
 * Redaction contracts (T-036 · 04 §7 · W3-CONTRACTS §8)
 * overlay ไม่แก้ต้นฉบับ — ผูกกับ scope (ทีม/คน/แผนก) apply ตอน serialize ตามสิทธิ์ผู้เรียก
 */
import { z } from "zod";

export const RedactionTargetType = z.enum(["field", "message_span", "attachment", "participant_email", "custom_field"]);
export type RedactionTargetType = z.infer<typeof RedactionTargetType>;

/** [T-063 R5] เพิ่ม "everyone" = ปกปิดกับทุก principal ที่เห็นการ์ด (ไม่ต้องระบุ principal_id ทีละราย) */
export const RedactionPrincipalType = z.enum(["user", "team", "department", "everyone"]);
export type RedactionPrincipalType = z.infer<typeof RedactionPrincipalType>;

export const RedactionItemInput = z
  .object({
    target_type: RedactionTargetType,
    /** message id (message_span) / attachment id / custom_field key / "subject" (field) / email (participant_email) */
    target_id: z.string().min(1).max(200),
    span_start: z.number().int().min(0).optional(),
    span_end: z.number().int().min(0).optional(),
    replacement: z.string().min(1).max(200).optional(),
  })
  .refine((v) => v.target_type !== "message_span" || (v.span_start !== undefined && v.span_end !== undefined && v.span_end > v.span_start), {
    message: "message_span ต้องระบุ span_start/span_end ที่ถูกต้อง",
    path: ["span_end"],
  });
export type RedactionItemInput = z.infer<typeof RedactionItemInput>;

/** everyone → ไม่ต้องมี principal_id (บังคับทุก principal) · user/team/department → ต้องมี */
export const RedactionScopeInput = z
  .object({
    principal_type: RedactionPrincipalType,
    principal_id: z.uuid().optional(),
  })
  .refine((v) => v.principal_type === "everyone" || v.principal_id !== undefined, {
    message: "scope แบบ user/team/department ต้องระบุ principal_id",
    path: ["principal_id"],
  });
export type RedactionScopeInput = z.infer<typeof RedactionScopeInput>;

/** scope ใน response — everyone คืน principal_id = null */
export const RedactionScope = z.object({
  principal_type: RedactionPrincipalType,
  principal_id: z.uuid().nullable(),
});
export type RedactionScope = z.infer<typeof RedactionScope>;

export const RedactionInput = z.object({
  handoff_id: z.uuid().optional(),
  reason: z.string().trim().min(1).max(500),
  items: z.array(RedactionItemInput).min(1).max(100),
  scopes: z.array(RedactionScopeInput).min(1).max(20),
  viewers: z.array(z.uuid()).max(50).optional(),
});
export type RedactionInput = z.infer<typeof RedactionInput>;

/**
 * ผู้ที่อยู่ใน scope: `target_id/span_start/span_end/replacement` = null (เห็นแค่ว่ามี item ที่ถูกปกปิด)
 * ผู้สร้าง/admin/นอก scope: เห็นเต็ม
 */
export const RedactionItem = z.object({
  id: z.uuid(),
  target_type: RedactionTargetType,
  target_id: z.string().nullable(),
  span_start: z.number().int().nullable(),
  span_end: z.number().int().nullable(),
  replacement: z.string().nullable(),
  redacted: z.literal(true),
});
export type RedactionItem = z.infer<typeof RedactionItem>;

export const Redaction = z.object({
  id: z.uuid(),
  ticket_id: z.uuid(),
  handoff_id: z.uuid().nullable(),
  created_by: z.uuid(),
  reason: z.string(),
  created_at: z.iso.datetime(),
  revoked_at: z.iso.datetime().nullable(),
  scopes: z.array(RedactionScope),
  viewers: z.array(z.uuid()),
  items: z.array(RedactionItem),
});
export type Redaction = z.infer<typeof Redaction>;

export const RedactionList = z.object({ items: z.array(Redaction) });
export type RedactionList = z.infer<typeof RedactionList>;

export const RevealInput = z.object({
  reason: z.string().trim().min(1).max(500),
  /** ไม่ระบุ = reveal ทั้งชุด */
  item_id: z.uuid().optional(),
});
export type RevealInput = z.infer<typeof RevealInput>;

export const RevealResult = z.object({ expires_at: z.iso.datetime() });
export type RevealResult = z.infer<typeof RevealResult>;

/** regex-only candidate ไฮไลต์ (AI = M3) — 04 §7.1 */
export const RedactionCandidate = z.object({
  message_id: z.uuid(),
  span_start: z.number().int(),
  span_end: z.number().int(),
  category: z.string(),
  text: z.string(),
  suggested_replacement: z.string(),
});
export type RedactionCandidate = z.infer<typeof RedactionCandidate>;

export const RedactionCandidateList = z.object({ items: z.array(RedactionCandidate) });
export type RedactionCandidateList = z.infer<typeof RedactionCandidateList>;
